import { useState, useMemo } from 'react';
import { LocationSelect as Select } from '@/lib/pages/forms/components';
import { Button } from '@/components/ui/button';
import {
  formatDateWithSlashesNoShift,
  toDateString,
} from '../../../utils/dates';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import { Loader2 } from 'lucide-react';

import {
  glacierPolicyApiGetPolicyQueryKey,
  glacierPolicyErpApiGetErpEligibleDatesOptions,
  glacierPolicyErpApiStartErpEndorsementMutation,
} from '@/lib/heyapi/@tanstack/react-query.gen';

import { Skeleton } from '@/components/ui/skeleton';

import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { toast } from '@/components/ui/use-toast';
import { useParams } from 'react-router';

import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import type { Policy } from '@/lib/heyapi';

export function ERPForm({ policy }: { policy: Policy | undefined }) {
  const form = useForm({});
  const { handleSubmit: hookFormSubmit } = form;
  const [selectedEffectiveDate, setSelectedEffectiveDate] = useState<
    string | null
  >(null);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isMutationInProgress, setIsMutationInProgress] = useState(false);
  const { id } = useParams();

  const { data: dates, isLoading } = useQuery({
    ...glacierPolicyErpApiGetErpEligibleDatesOptions({
      path: {
        policy_id: id!,
      },
    }),
  });

  const selectableData = useMemo(() => {
    return dates?.eligibleDates.map((date) => {
      return {
        value: formatDateWithSlashesNoShift(date),
        label: formatDateWithSlashesNoShift(date),
      };
    });
  }, [dates]);

  const disableConfirm =
    !selectedEffectiveDate || isLoading || isMutationInProgress;

  const queryClient = useQueryClient();

  const qk = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: id!,
    },
  });

  const handleCleanup = () => {
    setIsDialogOpen(false);
    setIsMutationInProgress(false);
    setSelectedEffectiveDate(null);
  };

  const saveErp = useMutation({
    ...glacierPolicyErpApiStartErpEndorsementMutation(),
    onError: (error) => {
      setIsMutationInProgress(false);
      toast({
        title: 'Error Creating ERP Endorsement',
        description: (
          <p className="mt-2 max-h-[40vh] w-[340px] rounded-md bg-red-600 p-4 text-white">
            {JSON.stringify(error)}
          </p>
        ),
      });
    },
    onSuccess: (_data) => {
      setTimeout(() => {
        handleCleanup();
      }, 100);
      toast({
        title: 'Success',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="text-white">Creating ERP Transaction</code>
          </pre>
        ),
      });

      // Open the Preview Page and reload the page
      setTimeout(() => {
        window.location.href = `/policy/${id}/erp`;
      }, 500);
    },
  });

  const handleOpenChange = (open: boolean) => {
    if (!isMutationInProgress) {
      setIsDialogOpen(open);

      if (!open) {
        handleCleanup();
      }
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsMutationInProgress(true);

    const date = toDateString(new Date(selectedEffectiveDate!));

    saveErp.mutate({
      body: {
        effective_date: date,
      },
      path: {
        policy_id: id!,
      },
    });
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild className="z-10">
        <Button
          className="flex w-full justify-start border-0 bg-none outline-none"
          variant="ghost"
        >
          <span className="ml-2">Issue ERP</span>
        </Button>
      </DialogTrigger>

      <DialogContent>
        {isMutationInProgress ? (
          <div className="flex items-center gap-4">
            <Loader2 className="animate-spin" />
            Creating ERP Transaction…
          </div>
        ) : (
          <div className="flex flex-col">
            <p className="mb-8 text-lg font-semibold">
              Issue ERP – Select Effective Date
            </p>
            {isLoading ? (
              <div className="flex flex-col space-y-3">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-10 w-1/3" />
                  <Skeleton className="h-10 w-2/3" />
                </div>
              </div>
            ) : (
              <Form {...form}>
                <form
                  onSubmit={handleSubmit}
                  id="ERPForm"
                  className="flex flex-col gap-8"
                >
                  <Select
                    value={selectedEffectiveDate || ''}
                    options={selectableData}
                    onChangeWithoutDefaultValue={setSelectedEffectiveDate}
                    onChange={() => {}}
                    name="effectiveDate"
                    placeholder="Select"
                    label="Effective Date"
                    orientation="horizontal"
                    stretch
                    disabled={isMutationInProgress}
                    ignoreReadMode
                  />
                </form>
              </Form>
            )}

            <div className="mt-8 flex justify-end gap-4">
              <DialogClose asChild disabled={isMutationInProgress}>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button form="ERPForm" type="submit" disabled={disableConfirm}>
                Confirm
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
